import { useState } from "react";
import { Check, X } from "lucide-react";
import { F, In, Area, Sel, Btn, Toggle } from "./kit";

const PREREQS = [["brief_complete", "Brief complete"], ["assets_received", "Footage & assets received"], ["scope_approved", "Scope approved"], ["payment_or_approval", "Payment / approval received"]];

export const Prerequisites = ({ project, onToggle }) => (
  <ul className="space-y-1" data-testid="prerequisites">
    {PREREQS.map(([k, label]) => {
      const ok = project.prerequisites[k];
      return (
        <li key={k}>
          <button disabled={!!project.started_at} onClick={() => onToggle(k, !ok)} className="w-full flex items-center gap-3 py-2 text-sm text-left disabled:cursor-default" data-testid={`prereq-${k}`}>
            <span className={`w-5 h-5 flex items-center justify-center border ${ok ? "bg-[#0055FF] border-[#0055FF]" : "border-zinc-600"}`}>{ok ? <Check size={14} /> : <X size={12} className="text-zinc-600" />}</span>
            <span className={ok ? "text-white" : "text-zinc-400"}>{label}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

const toLocal = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 6e4).toISOString().slice(0, 16) : "");
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);

export const StartClock = ({ project, onStart }) => {
  const [deadline, setDeadline] = useState("");
  const [notify, setNotify] = useState(true);
  const ready = Object.values(project.prerequisites).every(Boolean);
  return (
    <div className="space-y-3 border-t border-[#27272A] pt-4" data-testid="start-clock">
      {project.turnaround_hours ? <p className="text-sm text-zinc-400">Standard turnaround: first draft within {project.turnaround_hours} hours of start.</p>
        : <p className="text-sm text-amber-300">No standard turnaround on this quote — enter the confirmed first-draft deadline.</p>}
      <F label="Override / set first-draft deadline"><input type="datetime-local" className="eb-input !py-2.5" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="start-deadline-input" /></F>
      <Toggle label="Email client" checked={notify} onChange={setNotify} testid="start-notify" />
      <Btn disabled={!ready} onClick={() => onStart({ deadline: fromLocal(deadline), notify_client: notify })} data-testid="start-clock-btn">Start clock</Btn>
      {!ready && <p className="text-xs text-zinc-600">All prerequisites must be met before the clock can start.</p>}
    </div>
  );
};

export const ReasonAction = ({ label, cta, onSubmit, withDate, testid, variant = "ghost" }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState("");
  const [notify, setNotify] = useState(true);
  if (!open) return <Btn variant={variant} onClick={() => setOpen(true)} data-testid={`${testid}-open`}>{label}</Btn>;
  const go = async () => { const ok = await onSubmit({ reason, notes: reason, deadline: fromLocal(date), notify_client: notify }); if (ok !== false) { setOpen(false); setReason(""); } };
  return (
    <div className="w-full space-y-2 border border-[#27272A] p-3" data-testid={`${testid}-form`}>
      {withDate && <input type="datetime-local" className="eb-input !py-2.5" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`${testid}-date`} />}
      <Area value={reason} onChange={setReason} rows={2} placeholder={withDate ? "Reason for adjustment" : "Reason / notes"} testid={`${testid}-reason`} />
      <Toggle label="Email client" checked={notify} onChange={setNotify} testid={`${testid}-notify`} />
      <div className="flex gap-2"><Btn onClick={go} disabled={reason.length < 3 || (withDate && !date)} data-testid={`${testid}-submit`}>{cta}</Btn><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn></div>
    </div>
  );
};

export const LinkForm = ({ onSubmit }) => {
  const [f, setF] = useState({ label: "", url: "", kind: "draft", notify_client: true });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const go = async () => { const ok = await onSubmit(f); if (ok !== false) setF({ ...f, label: "", url: "" }); };
  return (
    <div className="space-y-2" data-testid="link-form">
      <div className="grid grid-cols-3 gap-2">
        <Sel value={f.kind} onChange={set("kind")} options={[{ value: "draft", label: "Draft" }, { value: "final", label: "Final" }, { value: "asset", label: "Client asset" }]} testid="link-kind" />
        <div className="col-span-2"><In value={f.label} onChange={set("label")} placeholder="Label (e.g. Reel 1 v1)" testid="link-label" /></div>
      </div>
      <In value={f.url} onChange={set("url")} placeholder="https://" testid="link-url" />
      {f.kind !== "asset" && <Toggle label="Email client" checked={f.notify_client} onChange={set("notify_client")} testid="link-notify" />}
      <Btn onClick={go} disabled={f.label.length < 2 || !f.url.startsWith("https://")} data-testid="link-submit">Add link</Btn>
    </div>
  );
};

export { toLocal };
